import { ref } from "vue"
import { useUserStore } from "../stores/user"
import { url } from "../helper"
import { refreshTokenIfNeeded } from "../composables/useAuth"
import { isLoggedIn } from "../composables/useAuth"
import api from "../composables/useApi"
// import { useI18n } from "vue-i18n"

/**
 * useCurrentUser composable
 * ---------------------------------------
 * Purpose
 * - Centralizes the logic for retrieving the currently authenticated user
 *   and synchronizing it with the user store.
 * - Exposes reactive loading and error states to allow UIs to react to the
 *   asynchronous fetch lifecycle.
 *
 * How it works
 * - Reads the JWT from localStorage (key: "jwt"). If missing, it returns early.
 * - Decodes the JWT payload to extract the user UUID (without verifying the signature).
 * - Fetches the current user via Drupal JSON:API using the UUID and bearer token.
 * - Updates the preferred locale and user data in the Pinia store.
 * - Determines the selected user profile either from localStorage (key: "user_profile")
 *   or from the first profile associated to the user, and then fetches full profile data.
 * - Sets isLoggedIn to true on success, or false on failure.
 *
 * Parameters
 * - localeRef: a writable ref (likely from Vue's ref or toRef) that will be updated
 *   to reflect the user's preferred language code (preferred_langcode).
 *
 * Returns
 * - fetchCurrentUser: async function that triggers the flow described above.
 * - loading: ref<boolean> that is true while fetching.
 * - error: ref<string|null> that contains a user-friendly error string when the fetch fails.
 *
 * Notes / Caveats
 * - This function assumes a valid JWT structure and presence of "uuid" in its payload.
 */
export const useCurrentUser = (localeRef) => {
	// Indicates whether a request is in-flight. Useful for showing spinners in UI.
	const loading = ref(false)
	// Holds a user-friendly error message if something goes wrong during fetch.
	const error = ref<string | null>(null)
	// Access the centralized user store (Pinia) to sync authentication state and profile data.
	const userStore = useUserStore()

	/**
	 * fetchCurrentUser
	 * -----------------
	 * Tries to populate the user store with the current authenticated user and
	 * their selected profile.
	 *
	 * Behavior
	 * - If no JWT exists, the function returns immediately (no error set, no state change).
	 * - On success:
	 *   • Updates localeRef to the user's preferred language.
	 *   • Sets userStore.state.user and ensures selected_profile is fully loaded.
	 *   • Marks userStore.state.isLoggedIn = true.
	 * - On failure:
	 *   • Sets a friendly error message and marks isLoggedIn = false.
	 * - Loading is toggled appropriately via the loading ref.
	 */
	const fetchCurrentUser = async () => {
		// const { locale } = useI18n()

		// Retrieve the stored JWT. If absent, there is no authenticated user.
		const token = await refreshTokenIfNeeded()
		if (!token) return
		try {
			loading.value = true
			// Decode the JWT payload to extract user UUID. This does not validate the token.
			const payload = JSON.parse(atob(token.split(".")[1]))
			// Fetch the current user using Drupal JSON:API, with relationships for images included.
			const response = await api.get(
				`${url}/jsonapi/user/user/${payload.uuid}?jsonapi_include=1&include=subscriber_profile_profiles,subscriber_profile_profiles.field_profile_photo,subscriber_profile_profiles.field_profile_photo.field_media_image`
			)

			// Extract the user resource object from JSON:API response.
			const user = response.data.data
			// Update the provided locale ref to match the user's preferred language.
			// if (isLoggedIn()) {
			// 	localeRef.value = user.preferred_langcode
			// }
			if (isLoggedIn() === false) {
				const urlParams = new URLSearchParams(window.location.search)
				const langFromUrl = urlParams.get("lang")
				const defaultLanguage = langFromUrl || "el"
				localeRef.value = defaultLanguage
			} else {
				// locale.value = user.preferred_langcode
				localeRef.value = user.preferred_langcode
				localStorage.setItem("preferred_langcode", user.preferred_langcode)
			}

			// Sync user data to the store.
			userStore.state.user = response.data.data

			// Resolve the selected profile: use the one from localStorage if present,
			// otherwise default to the first profile on the user.
			if (user.subscriber_profile_profiles && user.subscriber_profile_profiles[0]) {
				// If no persisted profile is found, default to the first profile reference via the resolved key.
				if (!localStorage.getItem("user_profile")) {
					const firstProfileRef = user.subscriber_profile_profiles[0]
					userStore.state.selected_profile = { id: firstProfileRef.id }
				} else {
					userStore.state.selected_profile = { id: localStorage.getItem("user_profile") }
				}
				// Fetch full profile details for the selected profile using Profile module endpoint.
				const response = await api.get(
					`/jsonapi/profile/subscriber_profile/${userStore.state.selected_profile.id}?jsonapi_include=1&include=field_profile_photo,field_profile_photo.field_media_image`
				)
				userStore.state.selected_profile = response.data.data
			}
			// Mark the user as logged in upon successful fetching and store updates.
			userStore.state.isLoggedIn = true
		} catch (err: any) {
			// If the error is a 401 Unauthorized, it means the token is expired or invalid.
			if (err.response?.status === 401) {
				// Attempt to refresh the token and retry the fetchCurrentUser
				userStore.state.isLoggedIn = false
				await refreshTokenIfNeeded()
				return
			} else {
				// On any error, provide a generic message for the UI and mark as logged out.
				error.value = "Failed to fetch user info"
				userStore.state.isLoggedIn = false
			}
		} finally {
			// Always clear the loading state at the end of the attempt.
			loading.value = false
		}
	}

	return {
		fetchCurrentUser,
		loading,
		error,
	}
}
